// TODO: Move to hooks lib?
import { useCallback, useMemo, useState } from 'react';

interface AsyncFnResult<TData> {
  /** Whether the function has ever been called. */
  called: boolean;
  /** Whether the last call to the function is still loading. */
  loading: boolean;
  /** The data of the last call, if resolved. */
  data?: TData;
  /** The error of the last call, if resolved. */
  error?: unknown;
}

/** Internal state of a call. */
interface CallState<TData> {
  /** The promise returned by the call. */
  promise: Promise<TData>;
  /** Whether the promise is still loading. */
  loading: boolean;
  /** Data of the promise, if resolved. */
  data?: TData;
  /** Error of the promise, if resolved. */
  error?: unknown;
}

/**
 * Hook that wraps an async function with state about its last call.
 *
 * This hook serves the exact same purpose as `useLazyQuery` and `useMutation`,
 * except this hook works for any async function, instead of just for GraphQL.
 *
 * @param asyncFn async function to be wrapped
 */
export function useLazyAsyncFn<TArgs extends unknown[], TData>(
  asyncFn: (...args: TArgs) => Promise<TData>,
): [(...args: TArgs) => Promise<TData>, AsyncFnResult<TData>] {
  const [lastCall, setLastCall] = useState<CallState<TData>>();
  const data = useMemo<AsyncFnResult<TData>>(
    () => ({
      called: lastCall !== undefined,
      loading: lastCall?.loading || false,
      data: lastCall?.data,
      error: lastCall?.error,
    }),
    [lastCall],
  );
  const wrappedAsyncFn: (...args: TArgs) => Promise<TData> = useCallback(
    (...args) => {
      const promise = asyncFn(...args);
      setLastCall({
        promise,
        loading: true,
      });
      promise
        .then((data) => {
          setLastCall((lastCall) => {
            // Only update state if this promise is still the last call.
            if (lastCall?.promise === promise) {
              return {
                promise,
                loading: false,
                data,
              };
            }

            return lastCall;
          });
        })
        .catch((error) => {
          setLastCall((lastCall) => {
            // Only update state if this promise is still the last call.
            if (lastCall?.promise === promise) {
              return {
                promise,
                loading: false,
                error,
              };
            }

            return lastCall;
          });
        });
      return promise;
    },
    [asyncFn],
  );

  return [wrappedAsyncFn, data];
}
